import type { GetStaticPaths, GetStaticProps } from 'next';
import type { HeaderNav } from 'interfaces/navigation/Header';
import { setPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import type { FranchiseListSearch, FranchisePage } from 'interfaces/content/Ranking';
import { getFranchiseBySlugAndYear } from 'api/Ranking/getFranchiseBySlugAndYear';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import setFranchisePageAdConfig from 'services/Ad/config/setFranchisePageAdConfig';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import type { PreviewData } from 'interfaces/PreviewData';
import { getFranchiseYearSearch } from 'api/Ranking/getFranchiseListItems';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import dynamic from 'next/dynamic';
import { PianoPaywallPageType } from '@/app_lib/types/piano/Paywall';

const SimpleRankingPage = dynamic(() => import('pages/SimpleRanking/SimpleRankingPage'));
const RankingCompanyFranchisePage = dynamic(() => import('pages/RankingCompanyFranchise/RankingCompanyFranchisePage'));
interface FranchiseYear {
  franchise: FranchisePage;
  subDomain: string;
  franchiseSearch: FranchiseListSearch;
}

/**
 * Franchise Yearly Page (Company)
 * ranking/fortune500/2020
 * OR
 * Franchise Yearly Page (Person)
 * ranking/40-under-40/2021
 */
const FranchiseYear = ({ franchise, franchiseSearch, subDomain }: FranchiseYear) =>
  franchise.listType === 'grid' ? (
    <SimpleRankingPage
      franchiseList={franchise}
      subDomain={subDomain}
    />
  ) : (
    <RankingCompanyFranchisePage
      franchise={franchise}
      franchiseSearch={franchiseSearch}
    />
  );

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { year } = context.params as { year: string };
    const { franchiseSlug } = context.params as { franchiseSlug: string };
    const { previewData } = context as { previewData: PreviewData };
    const platformCD = 'own';
    const franchise = await getFranchiseBySlugAndYear(franchiseSlug, platformCD, year, previewData);
    const franchiseSearch = await safeAsync(
      () => getFranchiseYearSearch(franchiseSlug, year, previewData),
      {} as FranchiseListSearch,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(franchise),
      fallbackData.arcSpanPageConfig,
    );
    const dataLayer = safe(() => setPageGtmConfig(franchise), fallbackData.dataLayer);
    const headData = safe(() => setHeadData(franchise), fallbackData.headData);
    const pianoPageConfig = safe(
      () => setPianoConfig({ pageData: franchise, pageType: PianoPaywallPageType.Ranking }),
      fallbackData.pianoPageConfig,
    );
    const pageAdConfig: PageAdConfig = safe(() => setFranchisePageAdConfig(franchise), fallbackData.pageAdConfig);

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        franchise,
        franchiseSearch,
        headData,
        headerNav: menu,
        pageAdConfig,
        pianoPageConfig,
        subDomain: '',
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths: GetStaticPaths = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            franchiseSlug: '40-under-40',
            year: '2021',
          },
        },
        {
          params: {
            franchiseSlug: 'fortune500',
            year: '2021',
          },
        },
      ],
    };
  }

  return {
    fallback: 'blocking',
    paths: [],
  };
};

export default FranchiseYear;
